define('home/utils/trackingUtils',[
    'jquery',
    'underscore',

    'home/models/Session',

    'home/utils/sidebarUtils',
    'home/utils/navigationUtils',
    'core/analytics/jester',
    'core/bus',
], function (
    $,
    _,

    Session,

    sidebarUtils,
    navigationUtils,
    jester,
    bus
) {
    'use strict';

    // Holds page-specific params to add to track calls, or a promise
    // to be resolved with params. These are cleared out when the page
    // changes.
    var pageParams = {};

    var listeners = {
        'openSection': function () {
            jesterEmit({
                verb: 'open',
                object_type: 'section',
                object_id: jester.client.get('zone') + '/' + jester.client.get('section'),
            });
        },

        'viewSection': function () {
            jesterEmit({
                verb: 'view',
                object_type: 'section',
                object_id: jester.client.get('zone') + '/' + jester.client.get('section'),
            });
        },

        'viewFeed': function (fetchableCollectionView) {
            var params = {
                verb: 'open',
                object_type: 'section',
                object_id: jester.client.get('zone') + '/' + jester.client.get('section') + '/page-1',
            };

            if (fetchableCollectionView.isError())
                params.adjective = 'error';
            else if (!fetchableCollectionView.collection.length)
                params.adjective = 'empty';

            jesterEmit(params);
        },

        'viewMoreFeed': function (numPages) {
            jesterEmit({
                verb: 'open',
                object_type: 'section',
                object_id: jester.client.get('zone') + '/' + jester.client.get('section') + '/page-' + numPages,
            });
        },

        'removeFollower': function (follower) {
            jesterEmit({
                verb: 'remove-follower',
                object_type: 'user',
                object_id: follower.get('id'),
            });
        },

        // When the UI has successfully created a post.
        // The created post is passed as the first argument.
        'uiCallback:postCreated': function (post, params) {
            params = params || {};
            _.extend(params, {
                object_type: 'post',
                object_id: post.id,
                verb: 'post',
            });

            if (post.has('parent')) {
                // If post is a 'reply' then set
                // the target for the activity to
                // the parent post.
                params.target_type = 'comment';
                params.target_id = post.get('parent'); // parent is just the ID
            }

            jesterEmit(params);
        },

        // Like/dislike a comment
        'uiAction:postUpvote': function (post) {
            jesterEmit({
                verb: 'like',
                object_type: 'post',
                object_id: post.id,
            });
        },
        'uiAction:postUnvote': function (post) {
            // ActivityStreams has no verb for 'undislike'
            // so we don't bother differentiating for now.
            jesterEmit({
                verb: 'unlike',
                object_type: 'post',
                object_id: post.id,
            });
        },
        'uiAction:postDownvote': function (post) {
            jesterEmit({
                verb: 'dislike',
                object_type: 'post',
                object_id: post.id,
            });
        },
        'uiAction:clickLink': function (evt, params) {
            var $target = $(evt.currentTarget);
            var isInternal = navigationUtils.isInbound($target[0]);

            // Check if any of the link params exist as data attrs on the
            // clicked link. Add any specified params.
            _.each(linkParams, function (attrName, paramName) {
                var value = $target.attr(attrName);
                if (value)
                    params[paramName] = value;
            });

            jesterEmit(_.extend({
                verb: 'click',
                object_type: 'link',
                object_id: $target.attr('href'),
                adverb: isInternal ? 'internal' : 'external',
            }, params));
        },
        // Clicks on edit link
        'uiAction:postStartUpdate': function (post, params) {
            params = params || {};
            _.extend(params, {
                verb: 'click',
                adjective: 'edit',
                object_type: 'link',
                object_id: post.id,
            });
            jesterEmit(params);
        },
        'threadFavorited': function (thread) {
            jesterEmit({
                verb: 'like',
                object_type: 'thread',
                object_id: thread.id,
            });
        },
    };

    // The jester params that may be added as data attrs on a link
    // for which a click is being tracked
    var linkParams = {
        'forum_id': 'data-forum-pk',
        'thread_id': 'data-thread-id',
        'channel_slug': 'data-channel-slug',
    };

    // the keys for jesterZoneNames are regexps
    // to match the current url
    //
    // Set values to 'DNT' to disable jester tracking
    // for that url
    var jesterZoneNames = {
        'user': 'profile',
        '/by/\\w+': 'profile',
        'inbox': 'notifications',
        '/welcome/?$': 'welcome',
        '/explore(/.+)?': 'explore',
        'discussion': 'thread',
        'discuss': 'thread',
        'edit': 'thread',
        'forum': 'community',
        'topic': 'topic',
        'settings': 'settings',
        'discover': 'discover',
        '/channels(\\/(yours|explore))?\\/?(\\?.+)?$': 'discover',
        '/channels?/\\w+': 'channel',
        '/home/?$': 'activityfeed',
        'preload': 'DNT',
    };

    /**
     * Which params we allow sending to jester. There may be others
     * returned from page views, used by other tracking systems.
     * Use this to filter them out.
     */
    var jesterAllowedPageParams = [
        'channel_slug',
        'forum',
    ];

    // Fallback to n/a when no zone is found so that we
    // know when we are tracking events that
    // 1) need a proper zone OR
    // 2) shouldn't be tracked at all
    var defaultZoneName = 'n/a';

    /*
     * This must be used instead of jester.client.emit.
     * will not send events to jester if zone is DNT
     */
    var jesterEmit = function (trackingParams) {
        if (trackingParams.zone === 'DNT')
            return;

        // zone might not be specified in the trackingParams
        // so we have to check the default zone too
        if (!trackingParams.zone && jester.client.get('zone') === 'DNT')
            return;

        $.when(pageParams).then(function (pageParams) {
            // Emit the event, filtering the params to those allowed by jester
            // (There may be other page params used by other tracking systems)
            jester.client.emit(_.defaults(trackingParams, _.pick(pageParams, jesterAllowedPageParams)));
        });
    };

    return {
        init: function () {
            var settings =
                {
                    experiment: 'V2',
                    variant: 'active',
                    product: this.productName(),
                    page_referrer_url: window.document.referrer,
                };

            jester.client.set(settings);

            bus.on(listeners);
        },

        /*
         * if home is opened in an iframe in the embed, the product
         * should be bridge instead of home
         */
        productName: function () {
            return sidebarUtils.isSidebar() ? 'bridge' : 'home';
        },

        jesterEmit: jesterEmit,

        /**
         * Updates url based properties, and clears out page-specific
         * properties set from the previous page
         */
        pageChanged: function () {
            this.urlChanged();

            this.setSection();
            pageParams = {};
        },

        /**
         * Updates the url and zone on url change, since these 2
         * properties are set based on the url value.
         */
        urlChanged: function () {
            var url = window.document.location.href;
            var zone = _.find(jesterZoneNames, function (_zone, regex) {
                return new RegExp(regex).test(url);
            });

            jester.client.set({
                page_url: url,
                zone: zone || defaultZoneName,
            });
        },

        setSection: function (sectionName) {
            jester.client.set({ section: sectionName || 'default' });
        },

        setPageParams: function (paramsPromise) {
            pageParams = paramsPromise;
        },

        initUser: function (userId) {
            if (userId)
                jester.client.set('user_id', userId);
        },

        trackVisit: function () {
            if (!jester.client.has('zone'))
                this.urlChanged();

            var productName = this.productName();

            var jesterTrack = function () {
                jesterEmit({
                    verb: 'view',
                    object_type: 'product',
                    object_id: productName,
                });
            };

            var session = Session.get();
            session.loadPromise().always(_.bind(function () {
                this.initUser(session.user.get('id'));
                jesterTrack();
            }, this));
        },
    };
});

